import React, { ReactNode } from 'react';
import { cn } from 'utils/cn';

interface Props {
    label: string | null | undefined;
    startAdornment?: ReactNode;
    endAdornment: ReactNode;
    /** Whether the input shows its clear button next to the chevron, which the overlay has to stay clear of. */
    clearable: boolean;
}

const ADORNMENT_CLASS = 'pointer-events-auto inline-flex shrink-0 items-center gap-1';

/**
 * Renders the selected value as `{startAdornment}{label}{endAdornment}` side by side in the input's own box.
 *
 * A combobox input is a plain `<input>`, so it can hold text and nothing else; an adornment that is
 * a node — a chip, an icon — has to be drawn over the input instead. The host controls visibility
 * and turns the input's own text transparent while this is up. It is positioned against the input
 * group, inside the group's text padding on the left and clear of the trailing buttons on the right.
 * Events pass through (`pointer-events-none`) except on the adornments, whose own pointer events are
 * re-enabled so a chip's tooltip still fires on hover.
 */
const SelectedValueOverlay: React.FC<Props> = ({ label, startAdornment, endAdornment, clearable }) => (
    <div
        className={cn(
            'pointer-events-none absolute inset-y-0 left-2.5 flex items-center gap-1 overflow-hidden text-sm',
            clearable ? 'right-16' : 'right-8'
        )}
    >
        {startAdornment != null && <div className={ADORNMENT_CLASS}>{startAdornment}</div>}
        <div className="group-has-data-selection-placeholder/input-group:text-muted-foreground new:ease-out min-w-0 overflow-hidden text-ellipsis whitespace-nowrap transition-[color] duration-150">
            {label}
        </div>
        {endAdornment != null && <div className={ADORNMENT_CLASS}>{endAdornment}</div>}
    </div>
);

export default SelectedValueOverlay;
